import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import {
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    setQuery,
} from '../store/clientsSlice';
import {
    ClientsAPI,
    type Client,
    type ClientStatus,
    type CreateClientDto,
    type UpdateClientDto,
} from '../api/clients';
import ClientFormModal, { ClientFormMode } from '../components/ClientFormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import {
    Plus, Search, Filter, Pencil, Trash2, CheckCircle2, XCircle,
    RefreshCw,
} from 'lucide-react';
import moment from 'moment';
import { suspendClient, reactivateClient } from '../store/clientsSlice';
import { Ban, RotateCcw, Mail } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import RenewPlanModal from '../components/RenewPlanModal';
import { authApi } from '../utils/httpClient';
import { ApiResponse } from '@industronics/fids-utils';
import UpgradePlanModal from '../components/UpgradePlanModal';


export default function ClientsPage() {
    const dispatch = useDispatch<AppDispatch>();
    const { items, status, query } = useSelector((s: RootState) => s.clients);

    // Filters
    const [search, setSearch] = useState(query.search ?? '');
    const [statusFilter, setStatusFilter] = useState<ClientStatus | ''>('');
    const [activeOnly, setActiveOnly] = useState<boolean>(false);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<ClientFormMode>('create');
    const [editing, setEditing] = useState<Client | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] =
        useState<Partial<Record<keyof CreateClientDto, string>>>({});

    // Confirm dialog (delete)
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Optional sort
    const [sortKey, setSortKey] = useState<'clientName' | 'email' | ''>('');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
    const [statusTarget, setStatusTarget] = useState<Client | null>(null);
    const [statusAction, setStatusAction] = useState<'suspend' | 'reactivate'>('suspend');
    const [statusLoading, setStatusLoading] = useState(false);

    // resend activation link
    const [activationConfirmOpen, setActivationConfirmOpen] = useState(false);
    const [activationTarget, setActivationTarget] = useState<Client | null>(null);
    const [activationLoading, setActivationLoading] = useState(false);

    // ✅ Renew plan modal state
    const [renewOpen, setRenewOpen] = useState(false);
    const [renewTarget, setRenewTarget] = useState<Client | null>(null);

    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [upgradeTarget, setUpgradeTarget] = useState<Client | null>(null);
    const [plans, setPlans] = useState<any[]>([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await authApi.get<ApiResponse>("/plans"); // adjust endpoint
                if (res.data.success) setPlans(res.data.data);
            } catch {
                // optional toast
            }
        })();
    }, []);

    const openUpgradePlan = (row: Client) => {
        setUpgradeTarget(row);
        setUpgradeOpen(true);
    };


    useEffect(() => {
        dispatch(fetchClients({ search: query.search, status: query.status, isActive: query.isActive }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const refresh = () => {
        const q = {
            search: search || undefined,
            status: (statusFilter || undefined) as ClientStatus | undefined,
            isActive: activeOnly || undefined,
        };
        dispatch(setQuery(q));
        dispatch(fetchClients(q));
    };

    const openCreate = () => { setEditing(null); setModalMode('create'); setShowModal(true); };
    const startEdit = (row: Client) => { setEditing(row); setModalMode('edit'); setShowModal(true); };

    // map Client -> UpdateClientDto for editing
    const toCreateDto = (c: Client): UpdateClientDto => ({
        clientName: (c as any).name,
        email: c.email,
    });

    const handleModalSubmit = async (payload: CreateClientDto) => {
        try {
            setSubmitting(true);
            setFormError(null);
            setFieldErrors({});

            if (modalMode === 'edit' && editing) {
                const upd: UpdateClientDto = {
                    clientName: payload.clientName,
                    email: payload.email,
                };
                await dispatch(updateClient({ id: editing._id, payload: upd })).unwrap();
                toast.success('Client updated successfully');
            } else {
                await dispatch(createClient(payload)).unwrap();
                toast.success('Client created successfully');
            }

            setShowModal(false);
            setEditing(null);
        } catch (err: any) {
            const msg = err?.message || 'Something went wrong';
            const code = err?.status;

            if (code === 409 && /email/i.test(msg)) {
                setFieldErrors(prev => ({ ...prev, email: 'Email already exists' }));
            }
            setFormError(msg);
            toast.error(msg || 'Failed to save client');
        } finally {
            setSubmitting(false);
        }
    };

    const openStatusConfirm = (row: Client, action: 'suspend' | 'reactivate') => {
        setStatusTarget(row);
        setStatusAction(action);
        setStatusConfirmOpen(true);
    };

    const confirmStatusChange = async () => {
        if (!statusTarget) return;
        try {
            setStatusLoading(true);
            if (statusAction === 'suspend') {
                await dispatch(suspendClient(statusTarget._id)).unwrap();
                toast.success('Client suspended successfully');
            } else {
                await dispatch(reactivateClient(statusTarget._id)).unwrap();
                toast.success('Client reactivated successfully');
            }
        } catch (err: any) {
            console.error(err?.message || err);
            toast.error(err?.message || 'Failed to update client status');
        } finally {
            setStatusLoading(false);
            setStatusConfirmOpen(false);
            setStatusTarget(null);
        }
    };

    const openActivationConfirm = (row: Client) => {
        setActivationTarget(row);
        setActivationConfirmOpen(true);
    }

    const confirmSendActivation = async () => {
        if (!activationTarget) return;
        try {
            setActivationLoading(true);
            await ClientsAPI.sendActivationLink(activationTarget.email);
            toast.success('Activation link sent successfully');
        } catch (err: any) {
            console.error('Failed to send activation link:', err?.message || err);
            toast.error('Failed to send activation link');
        } finally {
            setActivationLoading(false);
            setActivationConfirmOpen(false);
            setActivationTarget(null);
        }
    };

    const openDeleteConfirm = (row: Client) => { setDeleteTarget(row); setConfirmOpen(true); };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await dispatch(deleteClient(deleteTarget._id)).unwrap();
            toast.success('Client deleted successfully');
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setDeleteTarget(null);
        }
    };

    const sortedItems = useMemo(() => {
        if (!sortKey) return items;
        const copy = [...items];
        copy.sort((a, b) => {
            const va = (a[sortKey] || '').toString().toLowerCase();
            const vb = (b[sortKey] || '').toString().toLowerCase();
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return copy;
    }, [items, sortKey, sortDir]);

    const toggleSort = (key: 'clientName' | 'email') => {
        if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
        else { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    };

    // ✅ Open renew modal
    const openRenewPlan = (row: Client) => {
        setRenewTarget(row);
        setRenewOpen(true);
    };

    return (
        <div className="p-6">
            <Toaster position="top-right" reverseOrder={false} />

            {/* Header */}
            <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">Clients</h1>
                    <p className="text-sm text-gray-500">Create and manage your clients.</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-white hover:opacity-90">
                    <Plus size={18} /><span>Add Client</span>
                </button>
            </div>

            {/* Filters */}
            <div className="mb-4 grid gap-3 md:grid-cols-4">
                <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-2.5"><Search size={18} className="text-gray-400" /></span>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by client name, email…"
                        className="w-full rounded-xl border px-10 py-2 focus:outline-none focus:ring-2 focus:ring-black/10"
                    />
                </div>

                <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-2.5"><Filter size={18} className="text-gray-400" /></span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as ClientStatus | '')}
                        className="w-full appearance-none rounded-xl border px-10 py-2 focus:outline-none focus:ring-2 focus:ring-black/10"
                    >
                        <option value="">All Status</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="SUSPENDED">SUSPENDED</option>
                        <option value="INVITED">INVITED</option>
                    </select>
                </div>

                <label className="inline-flex items-center gap-2 rounded-xl border px-3 py-2">
                    <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                    <span>Active only</span>
                </label>

                <button onClick={refresh} className="rounded-xl border px-4 py-2 hover:bg-gray-50">Apply Filters</button>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border bg-white">
                <div className="max-h-[70vh] overflow-auto">
                    <table className="min-w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide">
                            <tr>
                                <th className="px-4 py-3">
                                    <button onClick={() => toggleSort('clientName')} className="inline-flex items-center gap-1" title="Sort by client name">
                                        Name
                                    </button>
                                </th>
                                <th className="px-4 py-3">
                                    <button onClick={() => toggleSort('email')} className="inline-flex items-center gap-1" title="Sort by email">
                                        Email
                                    </button>
                                </th>

                                {/* ✅ Subscription column (shows plan details or device licensing) */}
                                <th className="px-4 py-3">Subscription</th>

                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Created at</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {status === 'loading' && (
                                <tr><td colSpan={7} className="px-4 py-6 text-center">Loading…</td></tr>
                            )}

                            {status !== 'loading' && sortedItems.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No clients found.</td></tr>
                            )}

                            {sortedItems.map((row, idx) => {

                                const subscriptionModel = (row as any)?.subscriptionModel;
                                const currentPlan = (row as any)?.currentPlan;

                                const planKey = currentPlan?.planKey;
                                const startAtRaw = currentPlan?.startAt;
                                const endAtRaw = currentPlan?.endAt;

                                const startAt = startAtRaw ? new Date(startAtRaw) : null;
                                const endAt = endAtRaw ? new Date(endAtRaw) : null;

                                const now = new Date();

                                const isPlanClient = subscriptionModel === "PLAN";

                                // Not activated = missing dates OR explicit status
                                const isNotActivated =
                                    isPlanClient &&
                                    (!startAt || !endAt || currentPlan?.status === "NOT_ACTIVATED" || currentPlan?.status === "NOT ACTIVATED");

                                // Expired = has endAt and endAt < now
                                const isExpired = isPlanClient && !!endAt && endAt.getTime() < now.getTime();

                                // Active = has both dates and within range
                                const isActivePlan =
                                    isPlanClient &&
                                    !!startAt &&
                                    !!endAt &&
                                    startAt.getTime() <= now.getTime() &&
                                    endAt.getTime() >= now.getTime();

                                const subscriptionLabel =
                                    subscriptionModel === "DEVICE"
                                        ? "Device licensing"
                                        : isPlanClient
                                            ? (planKey ? `Plan: ${planKey}` : "Plan")
                                            : "—";

                                const validity =
                                    isActivePlan && endAt
                                        ? `Valid until ${moment(endAt).format("MMM DD, yyyy")}`
                                        : null;

                                const ctaText =
                                    isNotActivated && planKey
                                        ? `Activate ${planKey} plan`
                                        : isExpired && planKey
                                            ? `Renew ${planKey} plan`
                                            : null;


                                return (
                                    <tr key={row._id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                                        <td className="px-4 py-3">{(row as any).name}</td>
                                        <td className="px-4 py-3">{row.email}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                <span className="inline-flex w-fit rounded-full border px-2 py-1 text-xs">
                                                    {subscriptionLabel}
                                                </span>

                                                {/* ✅ Activate / Renew CTA */}
                                                {ctaText ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openRenewPlan(row)}
                                                        className="w-fit text-xs font-medium text-purple-700 hover:underline"
                                                        title={isNotActivated ? "Upload invoice & activate plan" : "Upload invoice & renew plan"}
                                                    >
                                                        {ctaText}
                                                    </button>
                                                ) : null}

                                                {/* ✅ Upgrade plan CTA (under subscription column) */}
                                                {isPlanClient && planKey && !isNotActivated && isActivePlan ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openUpgradePlan(row)}
                                                        className="w-fit text-xs font-medium text-blue-700 hover:underline"
                                                        title="Upload invoice & upgrade plan"
                                                    >
                                                        Upgrade plan
                                                    </button>
                                                ) : null}

                                                {validity ? <span className="text-xs text-gray-500">{validity}</span> : null}

                                                {isExpired ? <span className="text-xs text-amber-700">Expired</span> : null}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            {row.status ? (
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${row.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {row.status === 'ACTIVE' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                                                    {row.status}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>

                                        <td className="px-4 py-3">{moment((row as any).createdAt).format("MMM DD, yyyy")}</td>

                                        <td className="px-4 py-3">
                                            <div className="flex justify-end">
                                                <details className="relative">
                                                    <summary className="list-none cursor-pointer inline-flex items-center gap-2 rounded-lg border px-3 py-1 hover:bg-gray-50">
                                                        Actions
                                                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </summary>

                                                    {/* Menu */}
                                                    <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border bg-white shadow-lg">
                                                        <div className="py-1">
                                                            {(row as any).status === "INVITED" && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.preventDefault(); // prevent <details> toggling issues
                                                                        openActivationConfirm(row);
                                                                        (e.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                                                                    }}
                                                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                                                >
                                                                    <Mail size={16} />
                                                                    Resend Activation Link
                                                                </button>
                                                            )}

                                                            {(row as any).status === "ACTIVE" &&
                                                                (<button
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        openStatusConfirm(row, "suspend");
                                                                        (e.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                                                                    }}
                                                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-amber-700"
                                                                >
                                                                    <Ban size={16} />
                                                                    Suspend
                                                                </button>)
                                                            }

                                                            {(row as any).status === "SUSPENDED" &&
                                                                (<button
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        openStatusConfirm(row, "reactivate");
                                                                        (e.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                                                                    }}
                                                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-700"
                                                                >
                                                                    <RotateCcw size={16} />
                                                                    Reactivate
                                                                </button>)
                                                            }

                                                            <div className="my-1 h-px bg-gray-100" />

                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    startEdit(row);
                                                                    (e.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                                                                }}
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                                            >
                                                                <Pencil size={16} />
                                                                Edit
                                                            </button>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    openDeleteConfirm(row);
                                                                    (e.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                                                                }}
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                                            >
                                                                <Trash2 size={16} />
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                </details>
                                            </div>
                                        </td>

                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit */}
            <ClientFormModal
                open={showModal}
                mode={modalMode}
                initial={editing ? toCreateDto(editing) : undefined}
                submitting={submitting}
                error={formError}
                fieldErrors={fieldErrors}
                onClose={() => { setShowModal(false); setEditing(null); setFormError(null); setFieldErrors({}); }}
                onSubmit={handleModalSubmit}
            />

            {/* ✅ Renew Plan modal */}
            <RenewPlanModal
                open={renewOpen}
                client={renewTarget}
                onClose={() => {
                    setRenewOpen(false);
                    setRenewTarget(null);
                }}
                onSuccess={() => refresh()}
            />

            <UpgradePlanModal
                open={upgradeOpen}
                client={upgradeTarget}
                plans={plans}
                onClose={() => {
                    setUpgradeOpen(false);
                    setUpgradeTarget(null);
                }}
                onSuccess={() => refresh()}
            />



            {/* Delete confirm */}
            <ConfirmDialog
                open={confirmOpen}
                title="Delete record?"
                description={deleteTarget ? (
                    <span>
                        Are you sure you want to delete <b>{(deleteTarget as any).name}</b> ?
                    </span>
                ) : null}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                loading={deleting}
                onCancel={() => { setConfirmOpen(false); setDeleteTarget(null); }}
                onConfirm={confirmDelete}
            />

            {/* Send Activation Link Confirm Dialog */}
            <ConfirmDialog
                open={activationConfirmOpen}
                title="Send Activation Link?"
                description={
                    activationTarget ? (
                        <span>
                            This will send an activation link to <strong>{activationTarget.email}</strong>.
                            The link will be valid for a few hours.
                        </span>
                    ) : null
                }
                confirmText="Send Link"
                cancelText="Cancel"
                variant="default"
                loading={activationLoading}
                onCancel={() => { setActivationConfirmOpen(false); setActivationTarget(null); }}
                onConfirm={confirmSendActivation}
            />

            <ConfirmDialog
                open={statusConfirmOpen}
                title={statusAction === 'suspend' ? 'Suspend client?' : 'Reactivate client?'}
                description={
                    statusTarget ? (
                        <span>
                            {statusAction === 'suspend'
                                ? <>This will set <strong>{(statusTarget as any).clientName}</strong> to <strong>SUSPENDED</strong>. Users linked to this client may lose access.</>
                                : <>This will set <strong>{(statusTarget as any).clientName}</strong> to <strong>ACTIVE</strong>.</>}
                        </span>
                    ) : null
                }
                confirmText={statusAction === 'suspend' ? 'Suspend' : 'Reactivate'}
                cancelText="Cancel"
                variant={statusAction === 'suspend' ? 'danger' : 'default'}
                loading={statusLoading}
                onCancel={() => { setStatusConfirmOpen(false); setStatusTarget(null); }}
                onConfirm={confirmStatusChange}
            />
        </div>
    );
}
